import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if previously dismissed (24h cooldown)
    const lastDismissed = localStorage.getItem('pwa-dismissed');
    if (lastDismissed && Date.now() - parseInt(lastDismissed) < 86400000) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 30s on page
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detect successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  if (isInstalled || dismissed || !showPrompt || !deferredPrompt) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-mlt-navy-dark border-[0.5px] border-white/10 rounded-xl p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-white/40 hover:text-white rounded-lg"
          data-testid="pwa-dismiss"
        >
          <X className="w-[18px] h-[18px]" strokeWidth={1.6} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-mlt-orange flex items-center justify-center shrink-0">
            <Smartphone className="w-6 h-6 text-white" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-white text-sm">Install MyLocalTradie</p>
            <p className="text-xs text-white/50 mt-0.5">
              Add to your home screen for quick access, offline support, and push notifications.
            </p>
            <button
              onClick={handleInstall}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-mlt-orange hover:bg-mlt-orange-hover text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              data-testid="pwa-install-btn"
            >
              <Download className="w-[18px] h-[18px]" strokeWidth={1.6} />
              Install App
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
